// IR-1288 — after an automatic logout, re-login must land the user back on the
// page they were looking at, not on Overview. The target path travels through
// the URL (`/login?redirect=/data`), which makes it attacker-controllable, so
// everything that reads it goes through `sanitizeRedirect` first.

/** Paths we never restore to — bouncing back into the login flow is a loop. */
const DENIED_PREFIXES = ["/login"] as const;

/** Reject control characters (incl. CR/LF) smuggled into the path. */
function hasControlChars(value: string): boolean {
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * Narrow an untrusted `redirect` value down to a safe, same-origin path.
 *
 * Accepts only a root-relative path (`/data`, `/data?tab=groups#row-3`).
 * Rejects, in particular:
 *   - `//evil.com` and `/\evil.com` — protocol-relative URLs the browser would
 *     happily treat as a different origin (classic open-redirect).
 *   - `https://evil.com`, `javascript:…` — anything carrying a scheme.
 *   - `/login…` — logging in only to be dropped back on the login page.
 *
 * Returns `undefined` when the value can't be trusted, so callers fall back to
 * the default landing route.
 */
export function sanitizeRedirect(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;

  const value = raw.trim();
  if (value.length === 0) return undefined;
  if (hasControlChars(value)) return undefined;

  if (!value.startsWith("/")) return undefined;
  // The second char decides protocol-relative-ness: `//host` and `/\host`.
  if (value[1] === "/" || value[1] === "\\") return undefined;

  const pathname = value.split(/[?#]/, 1)[0] ?? "";
  for (const denied of DENIED_PREFIXES) {
    if (pathname === denied || pathname.startsWith(`${denied}/`)) return undefined;
  }

  return value;
}

/** Where the user lands when there is no (usable) restore target. */
export const DEFAULT_LANDING = "/";

/**
 * The path to actually navigate to after a successful login.
 *
 * Sanitises again at the point of use rather than trusting that the route's
 * `validateSearch` already ran: on an initial page load (a pasted/deep-linked
 * `/login?redirect=…`) the component was observed receiving the raw parsed
 * search value, not the validated one — see the "ignores a hostile redirect"
 * case in session-guard.integration.test.tsx. Sanitising here makes the
 * open-redirect impossible regardless of when validation runs.
 */
export function restoreTarget(raw: unknown): string {
  return sanitizeRedirect(raw) ?? DEFAULT_LANDING;
}
